import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardPreview,
  CardFooter,
  Button,
  Body1,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  TableSelectionCell,
  Tooltip,
  TabList,
  Tab,
  Input,
} from "@fluentui/react-components";
import { DismissCircleRegular, SearchRegular } from "@fluentui/react-icons";
import { ProfileService } from "../../service/ProfileService";
import { AppContext } from "./context/AppContext";
import Header from "./Header";
import Loader from "./Loader";
import ErrorAlert from "./ErrorAlert";
import { extractArrayPayload, getApiErrorMessage } from "../../utils/apierror";
import { setStringToStorage, removeProfilePreference, getStringFromStorage } from "../../utils/storagehelper";
import { ProfileSettings } from "../../types/app.storage.enums";

import {
  saveCustomFieldMasterData,
  saveLicenseDetails,
  savePmeIndex,
  saveMeasurementDates,
  saveCurrency,
  saveAlternateBaseCurrency,
  saveCurrencyHedge,
} from "../../utils/profilepreferencesave";
import { useProfileStyles } from "./style/profile.style";
import { isValidUser } from "../../utils/common";

const Profile = () => {
  const [selectedProfile, setSelectedProfile] = useState<string>("");
  const [profilelist, setProfileList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const styles = useProfileStyles();
  const navigate = useNavigate();
  const context = useContext(AppContext);

  const handleIsValidUser = () => {
    if (isValidUser()) {
      navigate("/home");
    }
  };

  const handleListClick = (value) => {
    handleIsValidUser();
    setSelectedProfile(() => value);
  };

  const handleProfileSearchChange = (e) => {
    handleIsValidUser();
    setSearchText(() => e.target.value);
  };

  const handleProfileSearchClear = () => {
    handleIsValidUser();
    setSearchText(() => "");
  };

  const saveProfilePreference = (profileid) => {
    setIsLoading(true);
    removeProfilePreference();
    setStringToStorage(ProfileSettings.ProfileId, profileid);
    return Promise.all([
      saveLicenseDetails(profileid),
      saveCustomFieldMasterData(profileid),
      savePmeIndex(profileid),
      saveMeasurementDates(profileid),
      saveCurrency(profileid),
      saveAlternateBaseCurrency(profileid),
      saveCurrencyHedge(profileid),
    ])
      .then(() => {
        navigate("/tabmenu");
      })
      .catch((error) => {
        console.log("Error: " + error);
        navigate("/tabmenu");
      });
  };

  const handleProfileSelect = () => {
    if (isValidUser()) {
      navigate("/home");
    } else {
      if (selectedProfile != "") {
        const profile = profilelist.filter((item) => {
          return item.profileID === selectedProfile;
        });
        context.setSelectedProfile(profile[0]);
        saveProfilePreference(selectedProfile);
      }
    }
  };

  const describeProfileError = (error) => {
    const message = getApiErrorMessage(error, "Unable to load profiles. Please try again.");
    return error?.status ? `${message} (HTTP ${error.status})` : message;
  };

  const populateProfileList = () => {
    setErrorMessage("");
    setIsLoading(true);
    new ProfileService()
      .getProfiles()
      .then((profile) => {
        const profiles = extractArrayPayload(profile);
        if (!profiles) {
          throw new Error(getApiErrorMessage(profile, "Unable to load profiles. Please try again."));
        }
        const storedProfileId = getStringFromStorage(ProfileSettings.ProfileId);
        const storedProfile = profiles.find((item) => item.profileID === storedProfileId);

        if (storedProfile) {
          setSelectedProfile(storedProfile.profileID);
          context.setSelectedProfile(storedProfile);
          saveProfilePreference(storedProfile.profileID);
          return;
        }

        if (profiles.length === 1) {
          setSelectedProfile(profiles[0].profileID);
          context.setSelectedProfile(profiles[0]);
          saveProfilePreference(profiles[0].profileID);
          return;
        }

        setProfileList(profiles);
        setIsLoading(false);
        return;
      })
      .catch((error) => {
        console.log("Error: " + error);
        setErrorMessage(describeProfileError(error));
        setProfileList([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    populateProfileList();
  }, []);

  return (
    <>
      <Header></Header>
      <div className={styles.root}>
        {!isLoading && (
          <Card className={styles.card} appearance="subtle">
            <CardHeader header={<Body1>Select a profile to proceed</Body1>}></CardHeader>
            {profilelist.length > 0 && (
              <Input
                contentBefore={<SearchRegular></SearchRegular>}
                contentAfter={
                  <DismissCircleRegular className={styles.cursorPointer} onClick={handleProfileSearchClear} />
                }
                onChange={handleProfileSearchChange}
                value={searchText}
              />
            )}
            <CardHeader
              header={
                <TabList selectedValue="data" size="small">
                  <Tab dir="rtl" id="data" value="data">
                    Assigned Profiles
                  </Tab>
                </TabList>
              }
            ></CardHeader>
            <CardPreview className={styles.tableContainer}>
              {profilelist.length === 0 ? (
                <div className={styles.emptyState}>
                  <Body1 block className={styles.emptyStateText}>
                    {errorMessage || "No profiles are assigned to your account."}
                  </Body1>
                  <div>
                    <Button key="retry" size="small" onClick={populateProfileList} className={styles.button}>
                      Retry
                    </Button>
                  </div>
                </div>
              ) : (
                <Table aria-label="Table with single selection">
                  <TableBody>
                    {profilelist
                      .filter((profile) => {
                        if (
                          (searchText.length >= 3 &&
                            profile.profileName.toLowerCase().indexOf(searchText.toLowerCase()) != -1) ||
                          searchText.length < 3
                        )
                          return profile;
                      })
                      .map((item) => {
                        return (
                          <TableRow
                            key={item.profileID}
                            onClick={() => handleListClick(item.profileID)}
                            appearance={item.profileID === selectedProfile ? "brand" : "none"}
                            className={styles.cursorPointer}
                          >
                            <TableSelectionCell
                              checked={item.profileID === selectedProfile}
                              type="radio"
                              radioIndicator={{ "aria-label": "Select row" }}
                            />
                            <TableCell>
                              <Tooltip withArrow content={item.profileName} relationship="label">
                                <TableCellLayout truncate className={styles.cell}>
                                  {item.profileName}
                                </TableCellLayout>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              )}
            </CardPreview>
            <CardFooter dir="rtl" className={styles.cardFooter}>
              <Button
                key="select"
                size="small"
                appearance="primary"
                disabled={selectedProfile === ""}
                onClick={handleProfileSelect}
                className={styles.button}
              >
                Proceed
              </Button>
            </CardFooter>
          </Card>
        )}
        {isLoading && <Loader></Loader>}
        <ErrorAlert message={errorMessage} onDismiss={() => setErrorMessage("")}></ErrorAlert>
      </div>
    </>
  );
};
export default Profile;
